import {
    isOwnPanel,
    ownPanelId,
    type AxisSide,
    type ChartSeries,
    type FixedPanelId,
    type PanelAxis,
    type PanelId,
    type PanelPlan,
    type SeriesMoves,
} from './seriesTypes';

export const MAX_AXES_PER_PANEL = 2;

const FIXED_ORDER: readonly FixedPanelId[] = [
    'probability',
    'clock',
    'flow',
    'position',
];

const AXIS_SIDES: readonly AxisSide[] = ['left', 'right'];

function axisKey(series: ChartSeries): string | null {
    if (series.axis === 'hidden') return null;
    if (series.axis === 'own') return `${series.unit}:${series.id}`;
    return series.unit;
}

function fits(axes: PanelAxis[], series: ChartSeries): boolean {
    const key = axisKey(series);
    if (key === null) return true;
    if (axes.some(a => a.key === key)) return true;
    return axes.length < MAX_AXES_PER_PANEL;
}

function addToAxes(axes: PanelAxis[], series: ChartSeries): void {
    const key = axisKey(series);
    if (key === null) return;
    const existing = axes.find(a => a.key === key);
    if (existing) {
        existing.seriesIds.push(series.id);
        return;
    }
    axes.push({
        key,
        unit: series.unit,
        side: AXIS_SIDES[axes.length],
        seriesIds: [series.id],
    });
}

function wantedPanel(series: ChartSeries, moves: SeriesMoves): PanelId {
    const moved = moves[series.id];
    if (moved === undefined || !series.movable) return series.home;
    return moved;
}

export function planPanels(
    allSeries: readonly ChartSeries[],
    moves: SeriesMoves = {}
): PanelPlan[] {
    const live = allSeries.filter(s => s.hasData);
    const inputIndex = new Map(live.map((s, i) => [s.id, i]));
    const panels = new Map<PanelId, PanelPlan>();
    const ownOrder: PanelId[] = [];

    const panel = (id: PanelId): PanelPlan => {
        let p = panels.get(id);
        if (!p) {
            p = { id, series: [], axes: [] };
            panels.set(id, p);
            if (isOwnPanel(id)) ownOrder.push(id);
        }
        return p;
    };

    const place = (series: ChartSeries, target: PanelId): void => {
        const p = panel(target);
        p.series.push(series);
        addToAxes(p.axes, series);
    };

    const placeOrOverflow = (series: ChartSeries, target: PanelId): void => {
        const existing = panels.get(target);
        if (existing && !fits(existing.axes, series)) {
            place(series, ownPanelId(series.id));
            return;
        }
        place(series, target);
    };

    const residents = live.filter(s => wantedPanel(s, moves) === s.home);
    const guests = live.filter(s => wantedPanel(s, moves) !== s.home);
    const homeless: ChartSeries[] = [];

    for (const s of residents.filter(r => r.axis !== 'own')) {
        placeOrOverflow(s, s.home);
    }

    for (const s of guests) {
        const target = wantedPanel(s, moves);
        if (isOwnPanel(target)) {
            place(s, target);
            continue;
        }
        const host = panels.get(target);
        if (!host || !fits(host.axes, s)) {
            homeless.push(s);
            continue;
        }
        place(s, target);
    }

    const ownAxisResidents = [
        ...residents.filter(r => r.axis === 'own'),
        ...homeless,
    ].sort((a, b) => (inputIndex.get(a.id) ?? 0) - (inputIndex.get(b.id) ?? 0));
    for (const s of ownAxisResidents) placeOrOverflow(s, s.home);

    const firstIndex = (ids: readonly string[]): number =>
        Math.min(...ids.map(id => inputIndex.get(id) ?? 0));
    for (const p of panels.values()) {
        p.series.sort(
            (a, b) => (inputIndex.get(a.id) ?? 0) - (inputIndex.get(b.id) ?? 0)
        );
        const home = (axis: PanelAxis): number =>
            axis.seriesIds.some(
                id => p.series.find(s => s.id === id)?.home === p.id
            ) || isOwnPanel(p.id)
                ? 0
                : 1;
        const own = (axis: PanelAxis): number =>
            axis.key.includes(':') ? 1 : 0;
        p.axes.sort(
            (a, b) =>
                home(a) - home(b) ||
                own(a) - own(b) ||
                firstIndex(a.seriesIds) - firstIndex(b.seriesIds)
        );
        p.axes.forEach((axis, i) => {
            axis.side = AXIS_SIDES[i];
        });
    }

    const ordered: PanelPlan[] = [];
    for (const id of FIXED_ORDER) {
        const p = panels.get(id);
        if (p) ordered.push(p);
    }
    for (const id of ownOrder) {
        const p = panels.get(id);
        if (p) ordered.push(p);
    }
    return ordered;
}

/**
 * The panels of a plan plus the ones still on their way (no data yet), in the
 * order the plan would give them once their data is in: the fixed panels
 * first, then the own panels as they come.
 */
export function withPending(
    planned: readonly PanelId[],
    pending: readonly PanelId[]
): PanelId[] {
    const all = new Set<PanelId>([...planned, ...pending]);
    const fixed = FIXED_ORDER.filter(id => all.has(id));
    const own = [...new Set([...planned, ...pending])].filter(isOwnPanel);
    return [...fixed, ...own];
}

export function panelOf(
    plan: readonly PanelPlan[],
    seriesId: string
): PanelId | null {
    return plan.find(p => p.series.some(s => s.id === seriesId))?.id ?? null;
}

export interface SeriesToggle {
    target: PanelId;
    possible: boolean;
}

export function seriesToggle(
    series: ChartSeries,
    allSeries: readonly ChartSeries[],
    moves: SeriesMoves = {}
): SeriesToggle | null {
    if (!series.movable || !series.hasData) return null;
    const current = panelOf(planPanels(allSeries, moves), series.id);
    const away: PanelId =
        series.home === 'probability' ? ownPanelId(series.id) : 'probability';
    const target = current === away ? series.home : away;
    const trial: Record<string, PanelId> = { ...moves };
    if (target === series.home) delete trial[series.id];
    else trial[series.id] = target;
    const landed = panelOf(planPanels(allSeries, trial), series.id);
    return { target, possible: landed === target };
}

/**
 * Moves a whole group of series at once: to the main chart, or each one back
 * to its home panel (which is the absence of a move). Moves of other series
 * stay as they are.
 */
export function moveGroup(
    moves: SeriesMoves,
    seriesIds: readonly string[],
    target: 'probability' | 'home'
): SeriesMoves {
    const next: Record<string, PanelId> = { ...moves };
    for (const id of seriesIds) {
        if (target === 'home') delete next[id];
        else next[id] = 'probability';
    }
    return next;
}

export interface GroupOnMain {
    /** Every series of the group that has data is drawn on the main chart. */
    on: boolean;
    /** Moving the whole group to the main chart would work: it has the axes. */
    possible: boolean;
}

export function groupOnMain(
    allSeries: readonly ChartSeries[],
    moves: SeriesMoves,
    seriesIds: readonly string[]
): GroupOnMain {
    const group = new Set(seriesIds);
    const members = allSeries.filter(s => group.has(s.id) && s.hasData);
    if (members.length === 0) return { on: false, possible: false };
    const landed = (trial: SeriesMoves): boolean => {
        const plan = planPanels(allSeries, trial);
        return members.every(s => panelOf(plan, s.id) === 'probability');
    };
    return {
        on: landed(moves),
        possible: landed(moveGroup(moves, seriesIds, 'probability')),
    };
}
